/**
 * One status vocabulary for the whole product.
 * Every page maps its domain status to one of six tones, so "Overdue" or "Paid" looks the
 * same in Contracts, Fleet, Fines, Custodies and Maintenance.
 *
 * Tones: success | warning | danger | info | accent | neutral
 */
const TONE_BY_STATUS = {
    // good / running
    open: 'success',
    active: 'success',
    available: 'success',
    paid: 'success',
    registered: 'success',
    success: 'success',
    completed: 'success',
    complete: 'success',
    done: 'success',
    approved: 'success',
    valid: 'success',
    ok: 'success',
    healthy: 'success',
    matched: 'success',
    synced: 'success',
    returned: 'success',
    settled: 'success',
    rented: 'accent',
    'on rent': 'accent',
    reserved: 'accent',
    booked: 'accent',
    monthly: 'accent',

    // needs attention
    pending: 'warning',
    draft: 'warning',
    queued: 'warning',
    partial: 'warning',
    'in progress': 'warning',
    processing: 'warning',
    workshop: 'warning',
    maintenance: 'warning',
    'in maintenance': 'warning',
    expiring: 'warning',
    'expiring soon': 'warning',
    review: 'warning',
    'needs review': 'warning',
    hold: 'warning',
    'on hold': 'warning',
    unmatched: 'warning',
    'pending approval': 'warning',

    // problem
    overdue: 'danger',
    expired: 'danger',
    failed: 'danger',
    failure: 'danger',
    error: 'danger',
    unpaid: 'danger',
    rejected: 'danger',
    blocked: 'danger',
    locked: 'danger',
    suspended: 'danger',
    damaged: 'danger',
    missing: 'danger',
    stolen: 'danger',
    'no match': 'danger',

    // informational
    new: 'info',
    'closing today': 'info',
    scheduled: 'info',
    upcoming: 'info',
    info: 'info',

    // finished / inactive
    closed: 'neutral',
    inactive: 'neutral',
    cancelled: 'neutral',
    canceled: 'neutral',
    archived: 'neutral',
    sold: 'neutral',
    'not sent': 'neutral',
    unknown: 'neutral',
};

export const TONES = ['success', 'warning', 'danger', 'info', 'accent', 'neutral'];

/** Normalise any status value ("OPEN", "in_progress", " Paid ") to a lookup key. */
const normalise = (status) => String(status ?? '').trim().toLowerCase().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ');

/** Tone for a status value. Unknown values are neutral. */
export function getStatusTone(status) {
    const key = normalise(status);
    if (!key) return 'neutral';
    if (TONE_BY_STATUS[key]) return TONE_BY_STATUS[key];
    // Prefix rules: "Overdue 2d", "Expired 3 days ago", "Open · monthly"
    const first = key.split(/[\s·,(]/)[0];
    return TONE_BY_STATUS[first] || 'neutral';
}

// Long statuses shortened so status columns stay narrow (the full word shows as a tooltip).
const SHORT_LABELS = {
    'preventive maintenance': 'Prev. Maint.',
};

/** Human label: "in_progress" -> "In progress", "OPEN" -> "Open". */
export function getStatusLabel(status) {
    const s = String(status ?? '').trim().replace(/_+/g, ' ');
    if (!s) return '—';
    const short = SHORT_LABELS[s.toLowerCase()];
    if (short) return short;
    if (s === s.toUpperCase() && s.length > 3) {
        const lower = s.toLowerCase();
        return lower.charAt(0).toUpperCase() + lower.slice(1);
    }
    return s.charAt(0).toUpperCase() + s.slice(1);
}
